/** В тестах меньше итераций PBKDF2, в prod — как в OWASP-подобных рекомендациях. */
const PBKDF2_ITERATIONS =
    typeof process !== 'undefined' && process.env.NODE_ENV === 'test' ? 10_000 : 250_000

/** v:1 — в AES уходит UTF-8 JSON; v:2 — сначала gzip(JSON), в AES уходит бинарный gzip (меньше данных для шифрования). */
export type EncryptedExportEnvelope = {
    v: 1 | 2
    alg: 'AES-GCM-PBKDF2'
    iter: number
    salt: string
    iv: string
    ct: string
}

export function uint8ToBase64(bytes: Uint8Array): string {
    const page = 0x8000
    const parts: string[] = []
    for (let i = 0; i < bytes.length; i += page) {
        parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + page) as unknown as number[]))
    }
    return btoa(parts.join(''))
}

export function base64ToUint8(b64: string): Uint8Array {
    const bin = atob(b64)
    const out = new Uint8Array(bin.length)
    for (let i = 0; i < bin.length; i++) {
        out[i] = bin.charCodeAt(i)
    }
    return out
}

/** Копия байтов с «обычным» ArrayBuffer — для subtle.* (TS иначе ругается на Uint8Array<ArrayBufferLike>). */
function toBufferSource(bytes: Uint8Array): BufferSource {
    return new Uint8Array(bytes)
}

function assertSubtle(): Crypto {
    const c = globalThis.crypto
    if (!c?.subtle) {
        throw new Error('Web Crypto недоступен: откройте приложение по HTTPS или с localhost')
    }
    return c
}

async function gzipUtf8ToBytes(text: string): Promise<Uint8Array> {
    if (typeof CompressionStream === 'undefined') {
        throw new Error('CompressionStream недоступен')
    }
    const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'))
    const buf = await new Response(stream).arrayBuffer()
    return new Uint8Array(buf)
}

export async function gunzipBytesToUtf8(gz: Uint8Array): Promise<string> {
    if (typeof DecompressionStream === 'undefined') {
        throw new Error('DecompressionStream недоступен (нужен современный браузер)')
    }
    const stream = new Blob([toBufferSource(gz)]).stream().pipeThrough(new DecompressionStream('gzip'))
    return new Response(stream).text()
}

export function isEncryptedFoodsFileJson(text: string): boolean {
    const t = text.trim()
    if (!t.startsWith('{')) {
        return false
    }
    try {
        return isEncryptedEnvelope(JSON.parse(t) as unknown)
    } catch {
        return false
    }
}

function isEncryptedEnvelope(x: unknown): x is EncryptedExportEnvelope {
    if (x === null || typeof x !== 'object') {
        return false
    }
    const o = x as Record<string, unknown>
    return (
        (o.v === 1 || o.v === 2) &&
        o.alg === 'AES-GCM-PBKDF2' &&
        typeof o.iter === 'number' &&
        o.iter >= 1 &&
        typeof o.salt === 'string' &&
        typeof o.iv === 'string' &&
        typeof o.ct === 'string'
    )
}

export type EncryptUtf8Options = {
    /** Сжать UTF-8 строку gzip перед AES (конверт v:2); нужен CompressionStream. */
    gzipBeforeEncrypt?: boolean
}

/** AES-256-GCM, ключ из пароля через PBKDF2. */
export async function encryptUtf8WithPassword(
    plainText: string,
    password: string,
    options?: EncryptUtf8Options,
): Promise<string> {
    const cryptoApi = assertSubtle()
    const enc = new TextEncoder()
    const gzipInner = Boolean(options?.gzipBeforeEncrypt && typeof CompressionStream !== 'undefined')
    const version: 1 | 2 = gzipInner ? 2 : 1
    const payload: BufferSource = gzipInner
        ? toBufferSource(await gzipUtf8ToBytes(plainText))
        : toBufferSource(enc.encode(plainText))

    const salt = cryptoApi.getRandomValues(new Uint8Array(16))
    const iv = cryptoApi.getRandomValues(new Uint8Array(12))
    const keyMaterial = await cryptoApi.subtle.importKey(
        'raw',
        toBufferSource(enc.encode(password)),
        'PBKDF2',
        false,
        ['deriveKey'],
    )
    const key = await cryptoApi.subtle.deriveKey(
        { name: 'PBKDF2', salt: toBufferSource(salt), iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt'],
    )
    const ciphertext = await cryptoApi.subtle.encrypt(
        { name: 'AES-GCM', iv: toBufferSource(iv) },
        key,
        payload,
    )
    const envelope: EncryptedExportEnvelope = {
        v: version,
        alg: 'AES-GCM-PBKDF2',
        iter: PBKDF2_ITERATIONS,
        salt: uint8ToBase64(salt),
        iv: uint8ToBase64(iv),
        ct: uint8ToBase64(new Uint8Array(ciphertext)),
    }
    return JSON.stringify(envelope, null, 2)
}

/** Расшифровка конверта из `encryptUtf8WithPassword` (тот же пароль и поле `iter` в JSON). */
export async function decryptUtf8WithPassword(encryptedText: string, password: string): Promise<string> {
    const cryptoApi = assertSubtle()
    let parsed: unknown
    try {
        parsed = JSON.parse(encryptedText) as unknown
    } catch {
        throw new Error('Файл не похож на JSON конверта')
    }
    if (!isEncryptedEnvelope(parsed)) {
        throw new Error('Неизвестный формат зашифрованного экспорта')
    }
    const enc = new TextEncoder()
    const dec = new TextDecoder()
    let salt: Uint8Array
    let iv: Uint8Array
    let ct: Uint8Array
    try {
        salt = base64ToUint8(parsed.salt)
        iv = base64ToUint8(parsed.iv)
        ct = base64ToUint8(parsed.ct)
    } catch {
        throw new Error('Некорректные поля salt/iv/ct (base64)')
    }
    if (iv.length !== 12) {
        throw new Error('Некорректная длина IV для AES-GCM')
    }
    const keyMaterial = await cryptoApi.subtle.importKey(
        'raw',
        toBufferSource(enc.encode(password)),
        'PBKDF2',
        false,
        ['deriveKey'],
    )
    const key = await cryptoApi.subtle.deriveKey(
        { name: 'PBKDF2', salt: toBufferSource(salt), iterations: parsed.iter, hash: 'SHA-256' },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt'],
    )
    let plainBuf: ArrayBuffer
    try {
        plainBuf = await cryptoApi.subtle.decrypt(
            { name: 'AES-GCM', iv: toBufferSource(iv) },
            key,
            toBufferSource(ct),
        )
    } catch {
        throw new Error('Не удалось расшифровать: неверный пароль или повреждённые данные')
    }
    if (parsed.v === 2) {
        try {
            return await gunzipBytesToUtf8(new Uint8Array(plainBuf))
        } catch {
            throw new Error('Не удалось распаковать gzip после расшифровки')
        }
    }
    return dec.decode(plainBuf)
}
